import React, { useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import classnames from 'classnames';

import classes from './Modal.scss';

type Props = {
    isOpen?: boolean;
    onDismiss?();
    type?: 'sidebar' | 'popup' | 'sidebar-small' | 'narrow' | 'popup-wide' | 'wide' | 'transparent' | 'very-wide';
    noAnimation?: boolean;
    className?: string;
    bottomSpacing?: boolean;
    accessibleTitle?: string;
    accessibleDescription?: string;
};

export const Modal: React.FC<React.PropsWithChildren<Props>> = ({
    isOpen = false,
    onDismiss,
    className,
    type = 'popup',
    noAnimation,
    children,
    accessibleTitle = 'Dialog',
    accessibleDescription = 'Dialog content',
}) => {
    const modalContentRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen && modalContentRef.current) {
            const modalContentElement = modalContentRef.current;

            let startY = 0;
            let startScrollTop = 0;
            let isScrolling = false;

            const handleTouchStart = (e: TouchEvent) => {
                startY = e.touches[0].clientY;
                startScrollTop = modalContentElement.scrollTop;
                isScrolling = false;
            };

            const handleTouchMove = (e: TouchEvent) => {
                if (!isScrolling) {
                    const deltaY = startY - e.touches[0].clientY;
                    modalContentElement.scrollTop = startScrollTop + deltaY;
                    e.preventDefault();
                }
            };

            const handleTouchEnd = () => {
                isScrolling = false;
            };

            modalContentElement.addEventListener('touchstart', handleTouchStart, { passive: false });
            modalContentElement.addEventListener('touchmove', handleTouchMove, { passive: false });
            modalContentElement.addEventListener('touchend', handleTouchEnd);

            return () => {
                modalContentElement.removeEventListener('touchstart', handleTouchStart);
                modalContentElement.removeEventListener('touchmove', handleTouchMove);
                modalContentElement.removeEventListener('touchend', handleTouchEnd);
            };
        }
    }, [isOpen]);

    return (
        <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onDismiss?.()}>
            <Dialog.Portal>
                <Dialog.Overlay
                    ref={overlayRef}
                    className={classnames(classes.modalOverlay, {
                        [classes.modalOverlay__noAnimation]: noAnimation,
                    })}
                    data-test-id="Modal"
                >
                    <Dialog.Content
                        className={classnames(classes.modalContainer, {
                            [classes.modalContainerPopup]: type === 'popup',
                            [classes.modalContainerPopupWide]: type === 'popup-wide',
                            [classes.modalContainerPopupNarrow]: type === 'narrow',
                            [classes.modalContainerSidebar]: type === 'sidebar' || type === 'sidebar-small',
                            [classes.modalContainerSidebarSmall]: type === 'sidebar-small',
                            [classes.modalContainerWide]: type === 'wide',
                            [classes.modalContainerVeryWide]: type === 'very-wide',
                            [classes.modalContainerTransparent]: type === 'transparent',
                        })}
                    >
                        <Dialog.Title className={classes.visuallyHidden}>{accessibleTitle}</Dialog.Title>
                        <Dialog.Description className={classes.visuallyHidden}>
                            {accessibleDescription}
                        </Dialog.Description>
                        <div ref={modalContentRef} className={classnames(className, classes.modalContent)}>
                            {children}
                        </div>
                        <ScrollLock />
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const ScrollLock: React.FC = () => {
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        const originalPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.paddingRight = originalPaddingRight;
        };
    }, []);
    return null;
};
